import React from "react";
import { graphql, useFragment } from "react-relay";

import { compact } from "lodash";

import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import {
	determineGroupIcon,
	determineGroupName,
	willGroupAcceptObject,
} from "~/nav/panels/Home/Sections/HomePanelGroup";
import Menu from "~/ui/menu";
import toasty from "~/ui/toast";

import CreateGroupButton from "./CreateGroupButton";
import HomePanelGroupMenuItem from "./HomePanelGroupMenuItem";

import type { HomePanelGroupsMenu_Mutation as HomePanelGroupsMenuMoveObjectMutation } from "~/__generated__/HomePanelGroupsMenu_Mutation.graphql";
import type { HomePanelGroupsMenu_object$key } from "~/__generated__/HomePanelGroupsMenu_object.graphql";
import type { HomePanelGroupsMenu_viewer$key } from "~/__generated__/HomePanelGroupsMenu_viewer.graphql";

export const addOrMoveObjectToGroupMutation = graphql`
	mutation HomePanelGroupsMenu_Mutation($groupId: ID!, $objectId: ID!) {
		addOrMoveToGroup(groupId: $groupId, objectId: $objectId) {
			viewer {
				...HomePanelGroupsMenu_viewer
				homePanelGroups {
					...HomePanelGroup_group
				}
			}
			movedObject {
				...HomePanelGroupsMenu_object
			}
		}
	}
`;

const viewerFragment = graphql`
	fragment HomePanelGroupsMenu_viewer on Viewer {
		homePanelGroups {
			id
			name
			type
			emojiName
		}
	}
`;

const objectFragment = graphql`
	fragment HomePanelGroupsMenu_object on HomePanelObjectInterface {
		id
		__typename
		... on NoteStream {
			type
		}
		homePanelGroup {
			id
			name
		}
	}
`;

type Props = {
	eventId?: string;
	/** either the Tag or NoteStream that this menu is accessed from */
	object?: HomePanelGroupsMenu_object$key | null;
	viewer: HomePanelGroupsMenu_viewer$key;
};

export default function HomePanelGroupsMenu(props: Props) {
	const { object, viewer, eventId } = props;

	const objectData = useFragment(objectFragment, object || null);
	const viewerData = useFragment(viewerFragment, viewer);

	const homePanelGroup = objectData?.homePanelGroup;
	const currentGroupId = homePanelGroup?.id;

	const [selectedGroup, setSelectedGroup] = React.useState<string | undefined>(currentGroupId);

	const addObjectToGroup = React.useCallback(
		async groupId => {
			try {
				let objectId: string;
				if (objectData) objectId = objectData.id;
				else if (eventId) objectId = eventId;
				else throw new Error("Cannot move unknown object to group");

				await commitMutationPromise<HomePanelGroupsMenuMoveObjectMutation>(environment, {
					mutation: addOrMoveObjectToGroupMutation,
					variables: { groupId, objectId },
				});
			} catch (e) {
				if (Array.isArray(e)) {
					for (let exception of e) {
						if ("message" in exception) toasty.error(exception.message);
					}
				}
			}
		},
		[eventId, objectData],
	);

	const groupMenuItems = compact(viewerData.homePanelGroups)
		.filter(group => willGroupAcceptObject(group, objectData))
		.map(
			g =>
				g &&
				g.type !== "OTHER_STREAMS" && (
					<HomePanelGroupMenuItem
						key={g.id}
						groupId={g.id}
						name={determineGroupName(g)}
						icon={determineGroupIcon(g)}
						add={addObjectToGroup}
					/>
				),
		);

	const defaultGroup = compact(viewerData.homePanelGroups).find(g => g.type === "OTHER_STREAMS");
	const setToDefault = React.useCallback(
		() => defaultGroup && addObjectToGroup(defaultGroup.id),
		[defaultGroup, addObjectToGroup],
	);

	const menuLabel = currentGroupId ? "Move to group" : "Add to group";

	return (
		<>
			<Menu.Label>{menuLabel}</Menu.Label>

			<Menu.RadioGroup value={selectedGroup} onValueChange={setSelectedGroup}>
				{groupMenuItems}
			</Menu.RadioGroup>
			{defaultGroup && homePanelGroup && homePanelGroup.id !== defaultGroup.id && (
				<>
					<Menu.Separator />
					<Menu.Item onSelect={setToDefault}>Remove from {objectData.homePanelGroup.name}</Menu.Item>
				</>
			)}
			<Menu.Separator />
			<CreateGroupButton eventId={eventId} object={objectData} />
		</>
	);
}
